import React from "react";
import { Box, Button } from "@mui/material";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import PublicFormGrid from "../../../components/publicForm/PublicFormGrid";
import PublicFormSection from "../../../components/publicForm/PublicFormSection";
import { APP_TOKENS } from "../../../components/publicForm/publicFormTokens";
import RffBooleanToggleField from "../../../components/finalForm/inputs/RffBooleanToggleField";
import RffDateField from "../../../components/finalForm/inputs/RffDateField";
import RffDollarField from "../../../components/finalForm/inputs/RffDollarField";
import RffSelectField from "../../../components/finalForm/inputs/RffSelectField";
import RffTextField from "../../../components/finalForm/inputs/RffTextField";
import RffConditional from "../../../components/finalForm/shared/RffConditional";
import LoanBreakdownPanel from "../components/LoanBreakdownPanel";
import LoanContactsSection from "../components/LoanContactsSection";
import { selectOptionsLoanProductType } from "../../../constants/selectOptions/selectOptionsLoanProductType";
import {
  CONSTRUCTION_PRODUCTS,
  DEFAULT_TYPE_OPTIONS,
  REFINANCE_PURPOSES,
} from "../applicationFormShared";

// Products financed as a single amount, as opposed to an initial amount plus a
// draw budget. Derived as the complement of CONSTRUCTION_PRODUCTS against the
// full product list so no product can fall through both branches and end up
// never being asked for a loan amount.
const SINGLE_AMOUNT_PRODUCTS = selectOptionsLoanProductType.filter(
  (product) => !CONSTRUCTION_PRODUCTS.includes(product),
);

/**
 * Step 3 — the money: amount requested, the loan being paid off, and who else is
 * on the deal. Product/purpose/exit/closing/broker are asked on step 1.
 *
 * `values` and `goToStep` are supplied by PublicFormShell.
 */
const StepLoanDetails = ({ values, goToStep }) => {
  // Every question on this step is gated on the product (and the Existing Loan
  // card on the purpose). Both are required on step 1, but a restored draft or a
  // rail jump can still land here without them — so say so rather than render an
  // empty card.
  const hasProduct = Boolean(values?.loanProductType);

  return (
    <Flx column>
      <PublicFormSection
        title="Amount Requested"
        description={
          hasProduct
            ? "These questions follow the product you chose on the first step."
            : undefined
        }
      >
        {hasProduct ? (
          <PublicFormGrid>
            <RffConditional
              field="loanProductType"
              operator="includes"
              value={SINGLE_AMOUNT_PRODUCTS}
            >
              <RffDollarField
                name="baseLoanAmount"
                label="Requested Loan Amount"
              />
            </RffConditional>

            <RffConditional
              field="loanProductType"
              operator="includes"
              value={CONSTRUCTION_PRODUCTS}
            >
              {/* Helper text is the glanceable one-liner; the `?` carries the
                  fuller explanation, so a row stays one line tall for the
                  applicants who already know what these mean. */}
              <RffDollarField
                size={4}
                name="baseLoanAmount"
                label="Initial loan amount"
                helperText="Funded at closing"
                hint="The amount funded at closing, before any construction draws are released."
              />
              <RffDollarField
                size={4}
                name="totalHoldback"
                label="Financed draw budget"
                helperText="Released through draws"
                hint="The total you're requesting to be released through construction draws over the life of the loan."
              />
              <RffDollarField
                size={4}
                name="totalConstructionBudget"
                label="Total construction budget"
                helperText="Full project cost"
                hint="Full project cost, including any portion you're funding yourself. Leave blank if draws cover 100%."
              />
              {/* Full width under the three amounts — the relationship between
                  them is what the applicant is actually deciding. */}
              <Box sx={{ gridColumn: "1 / -1" }}>
                <LoanBreakdownPanel />
              </Box>
            </RffConditional>
          </PublicFormGrid>
        ) : (
          <MissingProductNotice onGoBack={() => goToStep?.(0)} />
        )}
      </PublicFormSection>

      <RffConditional
        field="loanPurpose"
        operator="includes"
        value={REFINANCE_PURPOSES}
      >
        <PublicFormSection
          title="Existing Loan"
          description="Details of the loan this financing will pay off."
        >
          <PublicFormGrid>
            <RffDollarField
              name="outstandingLoanUpb"
              label="Total principal amount of outstanding loan (this would be your expected payoff)"
            />
            {/* Field name misspelling is the backend contract — do not "fix". */}
            <RffTextField
              name="exisitingLender"
              label="What is the name of the current lender?"
            />
            <RffDateField
              name="existingLoanMaturityDate"
              label="Maturity date of existing loan"
            />
            <RffBooleanToggleField
              name="existingLoanLatePayments"
              label="Have there ever been any payments made more than 30-days from due date on the existing loan?"
            />
            <RffBooleanToggleField
              name="existingLoanInDefault"
              label="Is the current loan in default (either payment or maturity default)?"
            />
            <RffConditional field="existingLoanInDefault" value={true}>
              <RffSelectField
                name="existingLoanDefaultType"
                label="Select existing loan default type"
                options={DEFAULT_TYPE_OPTIONS}
              />
            </RffConditional>
            <RffBooleanToggleField
              name="existingLoanHasUndrawnBudget"
              label="Does the existing loan have an undrawn budget?"
            />
            <RffConditional field="existingLoanHasUndrawnBudget" value={true}>
              <RffDollarField
                name="existingLoanUndrawnBudgetAmount"
                label="Existing loan undrawn budget amount"
              />
            </RffConditional>
            <RffBooleanToggleField
              name="propertyListedForSale"
              label="Is the property currently listed for sale?"
            />
            <RffBooleanToggleField
              name="propertyListedForSaleLastSixMonths"
              label="Has the property been listed for sale in the last six months?"
            />
          </PublicFormGrid>
        </PublicFormSection>
      </RffConditional>

      <PublicFormSection
        title="Working with anyone already?"
        description="Optional — add contacts only if you already have them lined up."
      >
        <LoanContactsSection />
      </PublicFormSection>
    </Flx>
  );
};

/** Shown when the product that drives this step's questions is missing. */
const MissingProductNotice = ({ onGoBack }) => (
  <Flx between centerY gap={2} wrap>
    <Txt component="div" sx={{ fontSize: 13.5, color: APP_TOKENS.inkMuted }}>
      Choose a loan product on the first step and the amount questions will
      appear here.
    </Txt>
    <Button variant="text" size="small" onClick={onGoBack}>
      Go to step 1
    </Button>
  </Flx>
);

export default StepLoanDetails;
